import { useEffect, useState } from "react";
import { Loader2, Save, User, X } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { apiGet, apiPut } from "@/lib/apiClient";

type UserProfile = {
  email: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneCountryCode?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  profilePhoto?: string | null;
};

type ProfileResponse = {
  global: UserProfile;
  overrideCount?: number;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProfileUpdate?: () => void;
};

export function ProfileSettingsDialog({ open, onOpenChange, onProfileUpdate }: Props) {
  const session = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [overrideCount, setOverrideCount] = useState(0);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      setIsLoading(true);
      setSaveError(null);
      const res = await apiGet<ProfileResponse>("/api/me/profile");
      if (cancelled) return;
      if (!res.ok || !res.data?.global) {
        setProfile(null);
        setIsLoading(false);
        return;
      }
      const g = res.data.global;
      setProfile(g);
      let nextFirst = g.firstName?.trim() || "";
      let nextLast = g.lastName?.trim() || "";
      if (!nextFirst && !nextLast && g.name?.trim()) {
        const parts = g.name.trim().split(/\s+/);
        nextFirst = parts[0] || "";
        nextLast = parts.slice(1).join(" ");
      }
      setFirstName(nextFirst);
      setLastName(nextLast);
      setPhoneCountryCode(g.phoneCountryCode ?? null);
      setPhoneNumber(g.phoneNumber ?? null);
      setDateOfBirth(g.dateOfBirth ?? null);
      setProfilePhoto(g.profilePhoto ?? null);
      setOverrideCount(res.data.overrideCount ?? 0);
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const saveGlobalProfile = async (confirmOverwriteOrgs: boolean) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const res = await apiPut<{
        error?: string;
        global?: UserProfile;
      }>("/api/me/profile", {
        scope: "global",
        confirmOverwriteOrgs,
        firstName: firstName || null,
        lastName: lastName || null,
        phoneCountryCode: phoneNumber ? phoneCountryCode : null,
        phoneNumber: phoneNumber || null,
        dateOfBirth: dateOfBirth || null,
        profilePhoto: profilePhoto || null,
      });

      if (res.status === 409) {
        const payload = res.data as { error?: string; overrideCount?: number } | null;
        if (payload?.error === "confirm_overwrite_required") {
          setOverrideCount(payload.overrideCount ?? overrideCount);
          setConfirmOverwriteOpen(true);
          return;
        }
      }

      if (!res.ok) {
        setSaveError(res.error || res.data?.error || "Failed to update profile");
        return;
      }

      if (res.data?.global) {
        setProfile(res.data.global);
      }
      setConfirmOverwriteOpen(false);
      session.refresh();
      onProfileUpdate?.();
      onOpenChange(false);
    } catch {
      setSaveError("Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  const displayPhoto = profilePhoto || session.data?.user?.image;
  const initials = (firstName || lastName || profile?.email || "?")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0]!)
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <button
          type="button"
          className="absolute inset-0 bg-gray-900/60"
          aria-label="Close profile settings"
          onClick={() => onOpenChange(false)}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-settings-title"
          className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-gray-900"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="profile-settings-title"
                className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white"
              >
                <User className="size-5" />
                Profile Settings
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Your Global Profile applies across One, Mail, and Pay. Per-organization names under
                This Organization override that default until you save the Global Profile again.
              </p>
            </div>
            <button
              type="button"
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="size-8 animate-spin text-gray-400" />
            </div>
          ) : profile ? (
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-4">
                {displayPhoto ? (
                  <img
                    src={displayPhoto}
                    alt=""
                    className="size-20 rounded-full border-2 border-gray-200 object-cover dark:border-gray-700"
                  />
                ) : (
                  <div className="flex size-20 items-center justify-center rounded-full border-2 border-gray-200 bg-indigo-600 text-xl font-semibold text-white dark:border-gray-700">
                    {initials}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">First name</span>
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Last name</span>
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                  />
                </label>
              </div>

              <label className="block text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">Email</span>
                <input
                  value={profile.email}
                  disabled
                  className="mt-1 w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-400"
                />
              </label>

              {saveError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
              ) : null}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/5"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                  disabled={isSaving}
                  onClick={() => {
                    if (overrideCount > 0) {
                      setConfirmOverwriteOpen(true);
                      return;
                    }
                    void saveGlobalProfile(false);
                  }}
                >
                  {isSaving ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 size-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">Failed to load profile</p>
          )}
        </div>
      </div>

      {confirmOverwriteOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-gray-900/70"
            aria-label="Dismiss"
            onClick={() => setConfirmOverwriteOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-gray-900">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Reset organization profiles?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Your Global Profile will apply everywhere
              {overrideCount > 0
                ? `, and custom settings in ${overrideCount} organization${overrideCount === 1 ? "" : "s"} will be cleared`
                : ""}
              . You can customize an organization again afterward.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium dark:border-white/20"
                disabled={isSaving}
                onClick={() => setConfirmOverwriteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                disabled={isSaving}
                onClick={() => void saveGlobalProfile(true)}
              >
                {isSaving ? "Saving…" : "Overwrite all orgs"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
